import { Payment } from "../../../../models/offer.model";
import { ProductCode } from "../../../../../product/product.model";
import { PaymentScheduleExportColumn } from "../../../../services/payment-schedule-export.service";

/**
 * Resolves the columns to export, mirroring exactly what the table renders for a
 * given product / view (see `offer-payment-schedule-table.component.html`):
 * - `totalPayback` is always CSS-hidden in the table and `discount` is never in
 *   `displayedColumns`, so neither is exported.
 * - `capitalRepayment` only shows (and exports) in the public/customer view.
 * - `payoff` only shows (and exports) for Term loans.
 *
 * Column order matches the table's `displayedColumns`.
 */
export const getPaymentScheduleExportColumns = (
  productCode: ProductCode,
  isPublicView: boolean
): PaymentScheduleExportColumn[] => {
  const columns: PaymentScheduleExportColumn[] = [
    { key: "date", type: "date", header: (): string => "Date", value: (p: Payment): Date => p.date },
    {
      key: "amount",
      type: "currency",
      header: (): string => "Amount",
      value: (p: Payment): number => (p.principalAmount ?? 0) + (p.interestAmount ?? 0),
    },
    {
      key: "principalAmount",
      type: "currency",
      header: (): string => "Principal",
      value: (p: Payment): number => p.principalAmount ?? 0,
    },
    {
      key: "interestAmount",
      type: "currency",
      header: (): string => "Interest",
      value: (p: Payment): number => p.interestAmount ?? 0,
    },
    {
      key: "balance",
      type: "currency",
      header: (isPublic: boolean): string => (isPublic ? "Closing balance" : "Balance"),
      value: (p: Payment): number => p.principalBalance ?? 0,
    },
    {
      key: "totalInterestCost",
      type: "currency",
      header: (): string => "Total interest cost",
      value: (p: Payment): number => p.interestPaid ?? 0,
    },
  ];

  if (isPublicView) {
    columns.push({
      key: "capitalRepayment",
      type: "currency",
      header: (): string => "Total capital repayment",
      value: (p: Payment): number => p.capitalRepayment ?? 0,
    });
  }

  if (productCode === ProductCode.Term) {
    columns.push({
      key: "payoff",
      type: "currency",
      header: (isPublic: boolean): string => (isPublic ? "Payoff amount" : "Discounted payoff"),
      value: (p: Payment): number => p.payoffAmount ?? 0,
    });
  }

  return columns;
};
